/**
 * routeInstances.ts — which deployment may reach which URL.
 *
 * Hiding a nav item stops people FINDING a feature. It does not stop a bookmark,
 * a stale Slack link, a browser autocomplete or a typed URL from rendering it.
 * This is the matching gate for the address bar, and the answer to "should I
 * render this link?" for the handful of link-lists that live outside navTree.
 *
 * ## How a path is resolved
 *
 * Longest matching prefix wins, so a Firefleet-only route nested under a shared
 * one still loses: `/customers` is on both, `/customers/transfers` is not.
 * A path matching NO rule is denied — allowlist, never denylist, same as nav.
 *
 * ## Why most of it is derived
 *
 * Every nav item and palette destination already declares its `instances`.
 * Re-declaring them here would be a second list to keep in step, so they are
 * read straight out of navTree. Only routes with no nav or palette home are
 * spelled out below.
 *
 * ## Adding a route
 *
 * If it has a nav or palette entry, nothing to do. If it does not, add it to
 * UNLISTED_ROUTES — `routeInstances.test.ts` fails the build otherwise.
 *
 * See docs/operations/two-instances.md.
 */
import { NAV_TREE, EXTRA_DESTINATIONS } from './navTree';
import { allowsInstance } from './menuConfig';
import { INSTANCE } from '../instance';
import type { Instance } from '../instance';

const BOTH = ['firefly', 'swarm'];

interface RouteRule {
  to: string;
  instances?: string[];
}

/**
 * Routes with no nav item and no palette entry — deep links, redirects, child
 * routes whose parent prefix does not already cover them, and the full-screen
 * shop displays.
 *
 * A child route needs an entry ONLY when its parent prefix does not reach it:
 * `/firmware/:hash` is covered by `/firmware`, but `/drone/:serial` has no
 * `/drone` nav item to sit under.
 */
const UNLISTED_ROUTES: RouteRule[] = [
  // --- always reachable -----------------------------------------------------
  { to: '/', instances: BOTH },
  { to: '/loading', instances: BOTH },
  { to: '/dashboard', instances: BOTH },        // redirect → /
  // Pre-auth. The guard only wraps the logged-in Switch so it never sees these;
  // listed anyway to keep the manifest exhaustive against App.js.
  { to: '/register', instances: BOTH },
  { to: '/resetpass', instances: BOTH },

  // --- drones ---------------------------------------------------------------
  // Swarm builds its own drones; the existing 142 D021 stayed in Firefleet's
  // database, but the surfaces are shared.
  { to: '/drone', instances: BOTH },            // detail, history, edit-history
  { to: '/add', instances: BOTH },              // /add/multiple
  // Board detail, reached by scanning a board sticker that has no drone behind
  // it. Rides with /drone: both products flash boards into their own airframes.
  { to: '/board', instances: BOTH },
  // A GPS, LED or pyro unit by its sticker, and the scan page the Inventory
  // button opens. Ride with /board and /inventory, which are both on both.
  { to: '/part', instances: BOTH },
  { to: '/parts/lookup', instances: BOTH },
  { to: '/parts/models', instances: BOTH },        // redirect → /parts/boms, covers /parts/models/:code

  // --- admin ----------------------------------------------------------------
  { to: '/admin', instances: BOTH },            // the hub itself
  { to: '/admin/users', instances: BOTH },
  { to: '/admin/ghost-flags', instances: BOTH },
  { to: '/admin/cards', instances: BOTH },      // company cards behind Receipts
  { to: '/admin/flerpie-debug' },               // no Flerpie on Swarm (D4)
  { to: '/admin/asana-import' },                // Spark is Firefleet only; reached from the Admin page
  { to: '/flerpie/pdf' },                       // an answer opened as a PDF; no Flerpie on Swarm (D4)
  { to: '/flerpie/times' },                     // a Flerpie Times edition; no Flerpie on Swarm (D4)

  // --- logistics ------------------------------------------------------------
  { to: '/packing', instances: BOTH },          // fulfillment, reached from Dispatch


  // --- Firefly-only ---------------------------------------------------------
  // Physical wall displays at Firefly HQ. No equivalent installation at Swarm.
  { to: '/shopscreen' },
  { to: '/shopscreen2' },
  { to: '/shop' },
  { to: '/office' },
  // Gold Stars: an internal recognition game. Self-contained cluster —
  // /connorstars issues, /leaderboards ranks, /timeline replays.
  { to: '/connorstars' },
  { to: '/leaderboards' },
  { to: '/timeline' },
  // Popup handoff for the Firefly Pilot GCS. Matched by the OUTER switch, above
  // the auth gate, so the guard never sees it either way.
  { to: '/pilot-auth' },
  // Bot sign-in consent. Matched by the outer switch like /pilot-auth.
  { to: '/oauth/consent' },
  // Pilot Simulator: the drone show game hidden behind seven taps on the dashboard
  // greeting (fun/PilotSimulator/WelcomeSecret.tsx). Same shape as Gold Stars above —
  // an internal game, Firefly only, with no nav entry by design, so it lives here
  // or the route test fails the build.
  { to: '/pilot-simulator' },
];

const RULES: RouteRule[] = [
  ...NAV_TREE.flatMap((d) => d.columns.flatMap((c) => c.items.map((i) => ({ to: i.to, instances: i.instances })))),
  ...EXTRA_DESTINATIONS.map((e) => ({ to: e.to, instances: e.instances })),
  ...UNLISTED_ROUTES,
];

const covers = (rule: RouteRule, path: string): boolean =>
  rule.to === path || (rule.to !== '/' && path.startsWith(rule.to + '/'));

/**
 * The rule governing a path, or null if nothing claims it. Exported for the
 * test that asserts every route in App.js is accounted for.
 */
export function resolveRouteRule(pathname: string): RouteRule | null {
  let best: RouteRule | null = null;
  for (const rule of RULES) {
    if (covers(rule, pathname) && (!best || rule.to.length > best.to.length)) best = rule;
  }
  return best;
}

/**
 * Instance-parameterised, for tests. Prefer routeAllowedOnInstance in app code.
 *
 * This used to short-circuit to `true` on Firefleet, because Firefleet was the
 * superset BY CONSTRUCTION — absent `instances` means Firefleet-only, so there
 * was nothing the gate could usefully deny there. `/serialmint` ended that: it
 * is `instances: ['swarm']`, and under the old shortcut a Firefleet user could
 * still reach it by typing the URL, hitting a database with no D-prefix fleet.
 *
 * So the manifest is now exhaustive for BOTH instances, and the coverage test
 * is what makes that safe — it parses App.js and fails on any unclaimed route.
 * An unclaimed path is denied on either instance now, not just on Swarm.
 */
export function allowedOn(inst: Instance, pathname: string): boolean {
  const rule = resolveRouteRule(pathname);
  return !!rule && allowsInstance(rule, inst);
}

/**
 * Can this URL be reached on THIS build? Used by the router guard in App.js and
 * by link-lists that navTree does not own (Quick Actions, the user menus).
 */
export const routeAllowedOnInstance = (pathname: string): boolean => allowedOn(INSTANCE, pathname);
